import type { LocationContext } from '../types';
import type { LocationProvider } from './LocationProvider';

// Covers the whole wait, including however long the browser's own
// permission dialog sits unanswered — 5s was too tight for a user to
// even notice/click a prompt, causing a spurious "no location" timeout
// on the very first (warm-up) call.
const DEFAULT_TIMEOUT_MS = 15000;
/** Accept a cached browser position up to this old, so repeated turns don't always re-prompt/re-fetch GPS. */
const MAX_POSITION_AGE_MS = 60000;

function isValidCoordinate(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

/**
 * Wraps navigator.geolocation.getCurrentPosition — a single, one-shot
 * reading, never navigator.geolocation.watchPosition. There is no
 * continuous tracking here: a fresh call is made only when the SDK
 * actually needs a location for a conversation turn.
 *
 * Never rejects: permission denial, unsupported environments, and timeouts
 * all resolve to null so a GPS failure can never fail the chatbot request.
 */
export class BrowserGpsLocationProvider implements LocationProvider {
  constructor(private readonly timeoutMs = DEFAULT_TIMEOUT_MS) {}

  getLocation(): Promise<LocationContext | null> {
    return new Promise((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        resolve(null);
        return;
      }

      try {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            if (!isValidCoordinate(latitude, longitude)) {
              resolve(null);
              return;
            }
            resolve({ latitude, longitude, source: 'gps', precision: 'precise' });
          },
          () => resolve(null), // permission denied, position unavailable, or timeout — all graceful
          { timeout: this.timeoutMs, maximumAge: MAX_POSITION_AGE_MS },
        );
      } catch {
        // No real browser throws here (the API always uses the callbacks), but guard anyway.
        resolve(null);
      }
    });
  }
}
